import { z } from 'zod';

/*
 * The two public intake forms, as one contract for the browser and the server.
 *
 * **`reference/prototype-copy/quote-form.md` is the specification**, captured
 * field by field off the live form on 2026-08-04. The roadmap's prose summary
 * of it is an orientation and is materially shorter — it describes "a lead with
 * the right source" and omits the per-property block that is the entire basis
 * of a quote.
 *
 * Written here rather than in the page so the same rules run in both places:
 * the form validates as somebody types, the API validates because a form is not
 * a security boundary, and neither can drift from the other.
 */

// ---------------------------------------------------------------------------
// The option lists the form offers
// ---------------------------------------------------------------------------

/** The seventeen bands, in the form's own order. Keys stored, labels rendered. */
export const SQUARE_FEET_BAND_OPTIONS = [
  { value: 'under_500', label: 'Under 500' },
  { value: '500_1000', label: '500–1,000' },
  { value: '1000_1500', label: '1,000–1,500' },
  { value: '1500_2000', label: '1,500–2,000' },
  { value: '2000_2500', label: '2,000–2,500' },
  { value: '2500_3000', label: '2,500–3,000' },
  { value: '3000_3500', label: '3,000–3,500' },
  { value: '3500_4000', label: '3,500–4,000' },
  { value: '4000_4500', label: '4,000–4,500' },
  { value: '4500_5000', label: '4,500–5,000' },
  { value: '5000_5500', label: '5,000–5,500' },
  { value: '5500_6000', label: '5,500–6,000' },
  { value: '6000_6500', label: '6,000–6,500' },
  { value: '6500_7000', label: '6,500–7,000' },
  { value: '7000_7500', label: '7,000–7,500' },
  { value: '7500_8000', label: '7,500–8,000' },
  { value: 'over_8000', label: 'Over 8,000' },
] as const;

export const SQUARE_FEET_BAND_VALUES = SQUARE_FEET_BAND_OPTIONS.map(
  (option) => option.value,
) as unknown as [string, ...string[]];

/** Never render a raw enum — CLAUDE.md §4. This is the display value. */
export function squareFeetBandLabel(value: string): string {
  return SQUARE_FEET_BAND_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

/**
 * Bathrooms in half steps: 1, 1.5, 2, … 10.
 *
 * Nineteen options, exactly as the form offers them. Sent as a string so the
 * decimal survives the round trip without a float ever existing — `2.5` in
 * JavaScript is fine, but the habit of turning a decimal column into a float
 * is the one CLAUDE.md warns about for money and it costs nothing to avoid.
 */
export const BATHROOM_OPTIONS = Array.from({ length: 19 }, (_, index) => {
  const value = 1 + index * 0.5;
  return {
    value: value.toFixed(1),
    label: Number.isInteger(value) ? String(value) : value.toFixed(1),
  };
});

export const BEDROOM_OPTIONS = Array.from({ length: 10 }, (_, index) => String(index + 1));

/** The live form goes to twenty. A property manager with twelve units is the point. */
export const MAX_PROPERTIES = 20;

export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const;

/** business-rules.md §1 — the form defaults to UT. */
export const DEFAULT_STATE = 'UT';

// ---------------------------------------------------------------------------
// Anti-spam — honeypot plus a timing check, and no CAPTCHA
// ---------------------------------------------------------------------------

/*
 * ROADMAP.md §8.6: "Rate-limited, honeypot plus a timing check rather than a
 * CAPTCHA."
 *
 * The reasoning is worth keeping beside the code. A CAPTCHA on the only form a
 * small cleaning business receives real work through is a tax on every genuine
 * customer — and the people most likely to fail one are the people least able
 * to ring instead. A honeypot and a clock cost the customer nothing and stop
 * the bulk of automated submissions, which is the actual threat here: nobody is
 * targeting this form, they are spraying every form.
 */

/** The field a human never sees and a bot fills in. Named to look worth filling. */
export const HONEYPOT_FIELD = 'company_website';

/**
 * How fast is too fast.
 *
 * Three seconds. Anyone genuinely typing a name, an email, a phone number and
 * a property's details takes longer, and a script posting the form takes
 * milliseconds. Deliberately generous: a false positive here is a lost customer.
 */
export const MIN_FILL_SECONDS = 3;

const antiSpamFields = {
  /*
   * **Deliberately not validated to be empty**, which looks like an oversight
   * and is the point.
   *
   * A `max(0)` here would reject a filled honeypot with a 400 and a validation
   * message — telling whoever wrote the bot that this field is the one that
   * caught them. Instead it is accepted by the schema, and `checkForSpam` drops
   * the submission behind a completely ordinary success response. The bot
   * learns nothing; a real customer never sees the field at all.
   *
   * Capped only so a megabyte in a hidden field cannot be used to make the
   * server do work.
   */
  [HONEYPOT_FIELD]: z.string().max(512).optional(),
  /** When the form was rendered, epoch ms. The timing check reads it. */
  renderedAt: z.number().int().positive().optional(),
};

// ---------------------------------------------------------------------------
// The forms
// ---------------------------------------------------------------------------

const nameField = z
  .string()
  .trim()
  .min(1, 'Please tell us your name.')
  .max(128, 'That name is too long for our system — please shorten it.');

const emailField = z
  .string()
  .trim()
  .min(1, 'We need an email address to reply to.')
  .max(255)
  .pipe(z.email('That does not look like an email address.'));

const phoneField = z
  .string()
  .trim()
  .min(7, 'That phone number looks too short.')
  .max(32, 'That phone number looks too long.');

/**
 * The quote form — `reference/prototype-copy/quote-form.md`.
 *
 * **Not a contact form with an extra box.** Name, email, phone, city, zip and
 * state are all required on the live form, then a property count and a
 * bedrooms/bathrooms/square-footage triple for each property up to twenty.
 */
export const quoteRequestSchema = z.object({
  name: nameField,
  email: emailField,
  phone: phoneField,

  // Required on the live form, so required here. Without them the office
  // cannot tell whether a lead is inside the service area — question 7.
  city: z.string().trim().min(1, 'Which city?').max(128),
  postalCode: z.string().trim().min(3, 'Which zip code?').max(16),
  state: z.enum(US_STATES),

  properties: z
    .array(
      z.object({
        bedrooms: z.coerce.number().int().min(1).max(10),
        // A string, matching the `decimal` column. `'2.5'` round-trips exactly.
        bathrooms: z
          .string()
          .regex(/^(?:10\.0|[1-9]\.[05]|[1-9]\.0)$/, 'Choose a number of bathrooms.'),
        squareFeetBand: z.enum(SQUARE_FEET_BAND_VALUES),
      }),
    )
    .min(1, 'Tell us about at least one property.')
    .max(MAX_PROPERTIES, `We can take up to ${String(MAX_PROPERTIES)} properties on one request.`),

  notes: z.string().trim().max(5000).optional(),

  ...antiSpamFields,
});

export type QuoteRequestInput = z.infer<typeof quoteRequestSchema>;

/**
 * The short contact form.
 *
 * §8.6 asks for both — "contact and quote forms both create a `lead` with the
 * right `source`" — and only the quote form exists on the live site. This is the
 * other half: somebody with a question rather than a property to price. It sets
 * `source = 'contact_form'`, which is what makes the two tellable apart in the
 * leads inbox.
 */
export const contactMessageSchema = z.object({
  name: nameField,
  email: emailField,
  phone: z.string().trim().max(32).optional(),
  message: z
    .string()
    .trim()
    .min(1, 'What can we help with?')
    .max(5000, 'That message is longer than our system can take — please shorten it.'),

  ...antiSpamFields,
});

export type ContactMessageInput = z.infer<typeof contactMessageSchema>;

/** What the public endpoints return. Deliberately says nothing about the record. */
export interface IntakeAccepted {
  ok: true;
  /** Shown back to the sender: "we will email you at …". */
  email: string;
}
